import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Users, Copy, CheckCircle, Gift, Share2, Coins, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Referral = () => {
  const { user, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (token) {
      axios.get(`${API}/referral/stats`, { headers: { Authorization: `Bearer ${token}` } }).then(r => setStats(r.data)).catch(() => {});
      axios.get(`${API}/referral/history`, { headers: { Authorization: `Bearer ${token}` } }).then(r => setHistory(r.data.referrals)).catch(() => {});
    }
  }, [token]);

  const referralLink = `${window.location.origin}/register?ref=${user?.referral_code || ''}`;

  const copyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast.success('Referral link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const copyCode = () => {
    navigator.clipboard.writeText(user?.referral_code || '');
    toast.success('Referral code copied!');
  };

  const shareTwitter = () => {
    const text = `Join me on All In Hand Universe and earn AIH tokens! Use my referral code: ${user?.referral_code}`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(referralLink)}`, '_blank');
  };

  return (
    <div className="min-h-screen pt-20 pb-12 px-6" data-testid="referral-page">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="font-unbounded text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <Users className="text-[#D4AF37]" /> Referral Program
          </h1>
          <p className="text-white/60">Invite friends and earn 2 AIH tokens for each successful referral!</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="glass-panel p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mx-auto mb-4">
              <Users size={24} className="text-[#D4AF37]" />
            </div>
            <p className="font-unbounded text-3xl font-bold text-white">{stats?.referral_count || 0}</p>
            <p className="text-sm text-white/60">Total Referrals</p>
          </div>

          <div className="glass-panel p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
              <Coins size={24} className="text-green-400" />
            </div>
            <p className="font-unbounded text-3xl font-bold text-green-400">+{stats?.referral_earnings || 0}</p>
            <p className="text-sm text-white/60">AIH Earned</p>
          </div>

          <div className="glass-panel p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center mx-auto mb-4">
              <Gift size={24} className="text-purple-400" />
            </div>
            <p className="font-unbounded text-3xl font-bold text-purple-400">{stats?.reward_per_referral || 2}</p>
            <p className="text-sm text-white/60">AIH Per Referral</p>
          </div>
        </div>

        <div className="glass-panel p-6 mb-8">
          <h2 className="font-unbounded text-xl font-bold text-white mb-4">Your Referral Code</h2>
          <div className="flex items-center gap-4 mb-4">
            <div className="flex-1 bg-[#0A1628] border border-[#D4AF37]/30 rounded-lg px-4 py-3">
              <p className="font-mono text-2xl text-[#D4AF37] font-bold tracking-wider">{user?.referral_code || 'Loading...'}</p>
            </div>
            <Button onClick={copyCode} className="btn-secondary">
              <Copy size={18} className="mr-2" /> Copy
            </Button>
          </div>

          <h3 className="font-semibold text-white mb-2">Share Your Link</h3>
          <div className="flex items-center gap-2">
            <Input value={referralLink} readOnly className="input-cosmic flex-1 text-sm" />
            <Button onClick={copyLink} className="btn-primary">
              {copied ? <CheckCircle size={18} /> : <Copy size={18} />}
            </Button>
            <Button onClick={shareTwitter} className="btn-secondary">
              <Share2 size={18} />
            </Button>
          </div>
        </div>

        <div className="glass-panel p-6 mb-8">
          <h2 className="font-unbounded text-xl font-bold text-white mb-4">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-10 h-10 rounded-full bg-[#D4AF37] text-black font-bold flex items-center justify-center mx-auto mb-3">1</div>
              <h3 className="font-semibold text-white mb-1">Share Your Link</h3>
              <p className="text-sm text-white/60">Copy your unique referral link and share it with friends</p>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 rounded-full bg-[#D4AF37] text-black font-bold flex items-center justify-center mx-auto mb-3">2</div>
              <h3 className="font-semibold text-white mb-1">Friends Sign Up</h3>
              <p className="text-sm text-white/60">They register using your link or referral code</p>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 rounded-full bg-[#D4AF37] text-black font-bold flex items-center justify-center mx-auto mb-3">3</div>
              <h3 className="font-semibold text-white mb-1">Earn Rewards</h3>
              <p className="text-sm text-white/60">You get 2 AIH tokens for each successful signup!</p>
            </div>
          </div>
        </div>

        {history.length > 0 && (
          <div className="glass-panel p-6">
            <h2 className="font-unbounded text-xl font-bold text-white mb-4">Referral History</h2>
            <div className="space-y-3">
              {history.map((ref, i) => (
                <div key={i} className="flex items-center justify-between py-3 border-b border-white/10">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#D4AF37] flex items-center justify-center">
                      <span className="text-black font-bold text-sm">{ref.username?.charAt(0).toUpperCase()}</span>
                    </div>
                    <span className="text-white">{ref.username}</span>
                  </div>
                  <span className="text-green-400 font-semibold">+2 AIH</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Referral;
